import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMapPin, FiUser, FiHeart, FiCheck, FiX } from 'react-icons/fi';
import { GiLovers, GiHearts, GiRing } from 'react-icons/gi';
import axios from 'axios';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import DateTimePicker from '../components/common/DateTimePicker';
import { matchMakingAPI } from '../services/api';
import './MatchMaking.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Rashi English-transliteration to Tamil
const RASHI_TAMIL = {
  'Mesham': 'மேஷம்', 'Rishabam': 'ரிஷபம்', 'Mithunam': 'மிதுனம்',
  'Katagam': 'கடகம்', 'Simham': 'சிம்மம்', 'Kanni': 'கன்னி',
  'Thulam': 'துலாம்', 'Vrischigam': 'விருச்சிகம்', 'Dhanusu': 'தனுசு',
  'Magaram': 'மகரம்', 'Kumbham': 'கும்பம்', 'Meenam': 'மீனம்',
  // English names fallback
  'Aries': 'மேஷம்', 'Taurus': 'ரிஷபம்', 'Gemini': 'மிதுனம்',
  'Cancer': 'கடகம்', 'Leo': 'சிம்மம்', 'Virgo': 'கன்னி',
  'Libra': 'துலாம்', 'Scorpio': 'விருச்சிகம்', 'Sagittarius': 'தனுசு',
  'Capricorn': 'மகரம்', 'Aquarius': 'கும்பம்', 'Pisces': 'மீனம்',
};

// Nakshatra to Tamil
const NAKSHATRA_TAMIL = {
  'Aswini': 'அசுவினி', 'Bharani': 'பரணி', 'Karthigai': 'கார்த்திகை',
  'Rohini': 'ரோகிணி', 'Mrigasheersham': 'மிருகசீரிஷம்', 'Thiruvathirai': 'திருவாதிரை',
  'Punarpoosam': 'புனர்பூசம்', 'Poosam': 'பூசம்', 'Aayilyam': 'ஆயில்யம்',
  'Makam': 'மகம்', 'Pooram': 'பூரம்', 'Uthiram': 'உத்திரம்',
  'Hastham': 'அஸ்தம்', 'Chithirai': 'சித்திரை', 'Swaathi': 'சுவாதி',
  'Vishakham': 'விசாகம்', 'Anusham': 'அனுஷம்', 'Kettai': 'கேட்டை',
  'Moolam': 'மூலம்', 'Pooradam': 'பூராடம்', 'Uthiraadam': 'உத்திராடம்',
  'Thiruvonam': 'திருவோணம்', 'Avittam': 'அவிட்டம்', 'Sathayam': 'சதயம்',
  'Pooratathi': 'பூரட்டாதி', 'Uthirattathi': 'உத்திரட்டாதி', 'Revathi': 'ரேவதி',
};

// Planet English to Tamil
const PLANET_TAMIL = {
  'ASC': 'லக்னம்', 'Sun': 'சூரியன்', 'Moon': 'சந்திரன்', 'Mars': 'செவ்வாய்',
  'Mercury': 'புதன்', 'Jupiter': 'குரு', 'Venus': 'சுக்கிரன்', 'Saturn': 'சனி',
  'Rahu': 'ராகு', 'Ketu': 'கேது'
};

const toTamil = (map, val) => map[val] || val;

/** City search hook - reusable for bride and groom */
function useCitySearch() {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCities = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/panchang/cities`, {
        params: { q: query }
      });
      setSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (input.length >= 2 && !selectedLocation) {
        searchCities(input);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [input, searchCities, selectedLocation]);

  const handleInputChange = (value) => {
    setInput(value);
    setSelectedLocation(null);
  };

  const handleSelect = (city) => {
    const displayName = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    setInput(displayName);
    setSelectedLocation(city);
    setShowSuggestions(false);
  };

  return { input, suggestions, showSuggestions, selectedLocation, ref, handleInputChange, handleSelect };
}

const JathagamPoruthamTamil = () => {
  const [brideData, setBrideData] = useState({ name: '', date: '', time: '' });
  const [groomData, setGroomData] = useState({ name: '', date: '', time: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (result && resultRef.current) {
      setTimeout(() => {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [result]);

  const brideCity = useCitySearch();
  const groomCity = useCitySearch();
  const hydrated = useHydrated();

  const handleBrideChange = (e) => {
    setBrideData({ ...brideData, [e.target.name]: e.target.value });
  };

  const handleGroomChange = (e) => {
    setGroomData({ ...groomData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const bride = {
        name: brideData.name,
        date: brideData.date,
        time: brideData.time,
        place: brideCity.input,
      };
      const groom = {
        name: groomData.name,
        date: groomData.date,
        time: groomData.time,
        place: groomCity.input,
      };

      if (brideCity.selectedLocation) {
        bride.lat = brideCity.selectedLocation.latitude;
        bride.lon = brideCity.selectedLocation.longitude;
        bride.tz = brideCity.selectedLocation.timezone;
        bride.place = brideCity.selectedLocation.city;
        bride.state = brideCity.selectedLocation.state;
        bride.country = brideCity.selectedLocation.country;
      }

      if (groomCity.selectedLocation) {
        groom.lat = groomCity.selectedLocation.latitude;
        groom.lon = groomCity.selectedLocation.longitude;
        groom.tz = groomCity.selectedLocation.timezone;
        groom.place = groomCity.selectedLocation.city;
        groom.state = groomCity.selectedLocation.state;
        groom.country = groomCity.selectedLocation.country;
      }

      const response = await matchMakingAPI.checkCompatibility({ bride, groom });
      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.error || 'பொருத்தம் சோதிக்க இயலவில்லை');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'பொருத்தம் சோதிக்க இயலவில்லை');
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (val) => {
    if (val === 3) return 'good';
    if (val === 2) return 'average';
    return 'poor';
  };

  const getStatusIcon = (val) => {
    if (val === 3) return <FiCheck />;
    if (val === 2) return '~';
    return <FiX />;
  };

  return (
    <div className="matchmaking-page">
      <SEO
        title="ஜாதக பொருத்தம் - 12 பொருத்தம் திருமணப் பொருத்தம்"
        description="இலவச தமிழ் 12 பொருத்த ஜாதக பொருத்தம் திருமணத்திற்கு. மணப்பெண் மற்றும் மணமகன் பொருத்தத்தை வேத ஜோதிடத்தின் மூலம் சோதிக்கவும். ராஜ்ஜு, நாடி மற்றும் மாங்கல்ய தோஷ பகுப்பாய்வு உடனடியாகப் பெறுங்கள்."
        keywords="ஜாதக பொருத்தம், 12 பொருத்தம், திருமணப் பொருத்தம், நட்சத்திர பொருத்தம், ராசி பொருத்தம், தமிழ் ஜோதிடம், திருமண பொருத்தம்"
        canonicalPath="/jathagam-porutham-in-tamil-for-marriage"
        locale="ta_IN"
        hreflang={[
          { lang: 'en', path: '/match-making' },
          { lang: 'ta', path: '/jathagam-porutham-in-tamil-for-marriage' },
        ]}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "ஜாதக பொருத்தம் சோதனை",
          "applicationCategory": "LifestyleApplication",
          "description": "தமிழ் 12 பொருத்த முறையில் திருமணப் பொருத்தம் சோதிக்கவும்",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {"@type": "Question", "name": "திருமணத்திற்கு நல்ல பொருத்த மதிப்பெண் என்ன?", "acceptedAnswer": {"@type": "Answer", "text": "12-ல் 10 அல்லது அதற்கு மேற்பட்ட மதிப்பெண் சிறப்பானதாகக் கருதப்படுகிறது. 8-9 நல்லது மற்றும் பொதுவாக திருமணத்திற்கு ஏற்றுக்கொள்ளத்தக்கது. 6-7 சாதாரணமானது மற்றும் தனிப்பட்ட காரணிகளை மதிப்பாய்வு செய்ய ஜோதிடரை அணுக வேண்டும். 6-க்கு கீழ் பொதுவாக பரிந்துரைக்கப்படுவதில்லை."}},
            {"@type": "Question", "name": "ராஜ்ஜு தோஷத்துடன் திருமணம் நடக்குமா?", "acceptedAnswer": {"@type": "Answer", "text": "ராஜ்ஜு தோஷம் பாரம்பரியமாக மிகவும் தீவிரமானதாகக் கருதப்படுகிறது. இருப்பினும், தனிப்பட்ட ஜாதகங்களில் சில கிரக நிலைகள் ராஜ்ஜு தோஷத்தை நடுநிலையாக்கலாம் — உதாரணமாக, குரு 7-ம் வீட்டைப் பார்த்தால் அல்லது சுக்கிரன் மிகவும் வலுவாக இருந்தால்."}},
            {"@type": "Question", "name": "மாங்கல்ய தோஷம் என்றால் என்ன?", "acceptedAnswer": {"@type": "Answer", "text": "மாங்கல்ய தோஷம் மற்றும் குஜ தோஷம் இரண்டும் ஒரே நிலையைக் குறிக்கின்றன — செவ்வாய் ஜாதகத்தின் 1, 2, 4, 7, 8 அல்லது 12-வது வீட்டில் அமர்ந்திருப்பது. இது திருமணத்தில் தடைகளை உருவாக்கும் என்று நம்பப்படுகிறது."}},
            {"@type": "Question", "name": "ஜாதக பொருத்தத்திற்கு என்ன தகவல் தேவை?", "acceptedAnswer": {"@type": "Answer", "text": "மணப்பெண் மற்றும் மணமகன் இருவருக்கும் சரியான பிறந்த தேதி, பிறந்த நேரம் மற்றும் பிறந்த இடம் தேவை. பிறந்த நட்சத்திரம் மற்றும் சந்திர ராசி இந்த தரவிலிருந்து தானாகவே கணக்கிடப்படும்."}}
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="header-icon">
            <GiLovers />
          </div>
          <h1>ஜாதக <span className="gradient-text">பொருத்தம்</span></h1>
          <p>தமிழ் 12 பொருத்த முறையில் திருமணப் பொருத்தம் சோதிக்கவும்</p>
        </motion.div>

        <div className="matchmaking-content">
          <motion.form
            className="matching-form"
            onSubmit={handleSubmit}
            initial={hydrated ? { opacity: 0, y: 20 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="form-columns">
              {/* Bride Details */}
              <div className="form-column bride-column">
                <div className="column-header">
                  <span className="column-icon bride-icon">👰</span>
                  <h3>மணப்பெண்</h3>
                </div>
                <div className="form-card">
                  <div className="form-group">
                    <label><FiUser className="input-icon" /> முழுப் பெயர்</label>
                    <input type="text" name="name" value={brideData.name} onChange={handleBrideChange} placeholder="மணப்பெண் பெயர்" required />
                  </div>
                  <DateTimePicker
                    date={brideData.date}
                    time={brideData.time}
                    onDateChange={(val) => setBrideData(prev => ({ ...prev, date: val }))}
                    onTimeChange={(val) => setBrideData(prev => ({ ...prev, time: val }))}
                  />
                  <div className="form-group city-search-group" ref={brideCity.ref}>
                    <label><FiMapPin className="input-icon" /> பிறந்த இடம்</label>
                    <input
                      type="text"
                      value={brideCity.input}
                      onChange={(e) => brideCity.handleInputChange(e.target.value)}
                      placeholder="நகரத்தைத் தேடவும்..."
                      required
                      autoComplete="off"
                    />
                    {brideCity.showSuggestions && brideCity.suggestions.length > 0 && (
                      <ul className="city-suggestions">
                        {brideCity.suggestions.map((city, i) => (
                          <li key={i} onClick={() => brideCity.handleSelect(city)}>
                            {city.city}{city.state ? `, ${city.state}` : ''}, {city.country}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Heart Connector */}
              <div className="heart-connector">
                <GiHearts className="heart-icon" />
              </div>

              {/* Groom Details */}
              <div className="form-column groom-column">
                <div className="column-header">
                  <span className="column-icon groom-icon">🤵</span>
                  <h3>மணமகன்</h3>
                </div>
                <div className="form-card">
                  <div className="form-group">
                    <label><FiUser className="input-icon" /> முழுப் பெயர்</label>
                    <input type="text" name="name" value={groomData.name} onChange={handleGroomChange} placeholder="மணமகன் பெயர்" required />
                  </div>
                  <DateTimePicker
                    date={groomData.date}
                    time={groomData.time}
                    onDateChange={(val) => setGroomData(prev => ({ ...prev, date: val }))}
                    onTimeChange={(val) => setGroomData(prev => ({ ...prev, time: val }))}
                  />
                  <div className="form-group city-search-group" ref={groomCity.ref}>
                    <label><FiMapPin className="input-icon" /> பிறந்த இடம்</label>
                    <input
                      type="text"
                      value={groomCity.input}
                      onChange={(e) => groomCity.handleInputChange(e.target.value)}
                      placeholder="நகரத்தைத் தேடவும்..."
                      required
                      autoComplete="off"
                    />
                    {groomCity.showSuggestions && groomCity.suggestions.length > 0 && (
                      <ul className="city-suggestions">
                        {groomCity.suggestions.map((city, i) => (
                          <li key={i} onClick={() => groomCity.handleSelect(city)}>
                            {city.city}{city.state ? `, ${city.state}` : ''}, {city.country}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group form-lang-selector">
              <label>மொழி</label>
              <select
                value="ta"
                onChange={(e) => {
                  if (e.target.value === 'en') {
                    navigate('/match-making');
                  }
                }}
              >
                <option value="en">English</option>
                <option value="ta">தமிழ் (Tamil)</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary btn-match" disabled={loading}>
              <FiHeart /> {loading ? 'சோதிக்கிறது...' : 'பொருத்தம் சோதிக்கவும்'}
            </button>

            {error && <div className="form-error-match">{error}</div>}
          </motion.form>

          {result && (
            <motion.div
              ref={resultRef}
              className="matching-results"
              initial={hydrated ? { opacity: 0, y: 30 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Birth Star Info */}
              <div className="star-info-card">
                <div className="star-info-row">
                  <div className="star-person bride-star">
                    <span className="star-label">மணப்பெண்</span>
                    <span className="star-name">{result.bride.name}</span>
                    <span className="star-detail">{toTamil(NAKSHATRA_TAMIL, result.bride.nakshatra)} - {toTamil(RASHI_TAMIL, result.bride.rashi)}</span>
                    <span className="star-detail">பதம் {result.bride.pada}</span>
                  </div>
                  <div className="star-vs">
                    <GiHearts />
                  </div>
                  <div className="star-person groom-star">
                    <span className="star-label">மணமகன்</span>
                    <span className="star-name">{result.groom.name}</span>
                    <span className="star-detail">{toTamil(NAKSHATRA_TAMIL, result.groom.nakshatra)} - {toTamil(RASHI_TAMIL, result.groom.rashi)}</span>
                    <span className="star-detail">பதம் {result.groom.pada}</span>
                  </div>
                </div>
              </div>

              {/* Score Card */}
              <div className="score-card">
                <div className="score-header">
                  <GiRing className="score-icon" />
                  <h3>பொருத்த மதிப்பெண்</h3>
                </div>
                <div className="score-circle">
                  <svg viewBox="0 0 100 100">
                    <circle className="score-bg" cx="50" cy="50" r="45" />
                    <circle
                      className="score-progress"
                      cx="50" cy="50" r="45"
                      style={{ strokeDasharray: `${result.percentage * 2.83} 283` }}
                    />
                  </svg>
                  <div className="score-text">
                    <span className="score-number">{result.totalScore}</span>
                    <span className="score-max">/ {result.maxScore}</span>
                  </div>
                </div>
                <div className="score-percentage">{result.percentage}% பொருத்தம்</div>
                <div className={`recommendation ${result.percentage >= 70 ? 'good' : result.percentage >= 50 ? 'average' : 'poor'}`}>
                  {result.recommendation}
                </div>
              </div>

              {/* 12 Porutham Details */}
              <div className="guna-details">
                <h3>12 பொருத்த பகுப்பாய்வு</h3>
                <p className="guna-subtitle">12 தமிழ் திருமணப் பொருத்த காரணிகளின் விரிவான பகுப்பாய்வு</p>

                <div className="guna-table">
                  <div className="guna-header">
                    <span>#</span>
                    <span>பொருத்தம்</span>
                    <span>விளக்கம்</span>
                    <span>முடிவு</span>
                  </div>
                  {result.poruthams && result.poruthams.map((p, index) => (
                    <motion.div
                      key={p.name}
                      className="guna-row"
                      initial={hydrated ? { opacity: 0, x: -20 } : false}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <span className="guna-num">{index + 1}</span>
                      <span className="guna-name">{p.name}</span>
                      <span className="guna-desc">{p.description}</span>
                      <span className={`guna-status ${getStatusClass(p.result)}`}>
                        {getStatusIcon(p.result)}
                      </span>
                    </motion.div>
                  ))}
                </div>

                <div className="porutham-summary">
                  <div className="summary-item good-count">
                    <FiCheck /> {result.goodCount} நல்லது
                  </div>
                  <div className="summary-item moderate-count">
                    ~ {result.moderateCount} சாதாரணம்
                  </div>
                  <div className="summary-item bad-count">
                    <FiX /> {result.badCount} பொருத்தமின்மை
                  </div>
                </div>
              </div>

              {/* Manglik Status */}
              {result.manglik && (
                <div className="manglik-card">
                  <h3>மாங்கல்ய தோஷம்</h3>
                  <div className="manglik-grid">
                    <div className="manglik-item">
                      <span className="manglik-label">மணப்பெண்</span>
                      <span className={`manglik-status ${result.manglik.bride.isManglik ? 'manglik' : 'non-manglik'}`}>
                        {result.manglik.bride.isManglik ? `மாங்கல்யம் உள்ளது (${result.manglik.bride.severity})` : 'மாங்கல்யம் இல்லை'}
                      </span>
                    </div>
                    <div className="manglik-item">
                      <span className="manglik-label">மணமகன்</span>
                      <span className={`manglik-status ${result.manglik.groom.isManglik ? 'manglik' : 'non-manglik'}`}>
                        {result.manglik.groom.isManglik ? `மாங்கல்யம் உள்ளது (${result.manglik.groom.severity})` : 'மாங்கல்யம் இல்லை'}
                      </span>
                    </div>
                  </div>
                  <div className={`manglik-compatibility ${result.manglik.compatible ? 'compatible' : 'incompatible'}`}>
                    {result.manglik.compatible ? (
                      <><FiCheck /> மாங்கல்ய தோஷம்: பொருத்தம்</>
                    ) : (
                      <><FiX /> மாங்கல்ய தோஷம்: பரிகாரம் தேவை</>
                    )}
                  </div>
                </div>
              )}

              {/* Interpretation */}
              <div className="interpretation-card">
                <h3>சுருக்கம்</h3>
                <p>
                  12 பொருத்த பகுப்பாய்வின் அடிப்படையில், இந்த ஜோடி <strong>{result.totalScore} / {result.maxScore}</strong> மதிப்பெண் ({result.percentage}%) பெற்றுள்ளது, இது <strong>{result.percentage >= 75 ? 'மிகவும் சிறந்த' : result.percentage >= 60 ? 'நல்ல' : result.percentage >= 50 ? 'சாதாரண' : 'சவாலான'}</strong> பொருத்தமாக கருதப்படுகிறது.
                  {result.goodCount >= 8 && ' இந்த ஜோடி பெரும்பாலான காரணிகளில் வலுவான பொருத்தத்தைக் காட்டுகிறது.'}
                  {result.badCount >= 4 && ' நல்லிணக்கமான உறவுக்கு பல பகுதிகளில் கவனம் தேவை.'}
                  {' '}விரிவான தனிப்பயன் பகுப்பாய்வு மற்றும் பரிகாரங்களுக்கு, ஒரு நிபுணர் ஜோதிடரை அணுகவும்.
                </p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Info Section */}
        <motion.div
          className="info-section"
          initial={hydrated ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h3>12 பொருத்தம் பற்றி</h3>
          <div className="info-grid">
            <div className="info-item">
              <h4>பொருத்தம் என்றால் என்ன?</h4>
              <p>பொருத்தம் என்பது மணப்பெண் மற்றும் மணமகனின் பிறந்த நட்சத்திரங்கள் (நக்ஷத்திரங்கள்) மற்றும் சந்திர ராசிகளின் அடிப்படையில் 12 வெவ்வேறு பொருத்தக் காரணிகளை மதிப்பீடு செய்யும் தமிழ் ஜாதக பொருத்த முறையாகும்.</p>
            </div>
            <div className="info-item">
              <h4>முக்கிய பொருத்தங்கள்</h4>
              <p>ராஜ்ஜு மற்றும் நாடி மிகவும் முக்கியமானவையாகக் கருதப்படுகின்றன. ராஜ்ஜு திருமண பந்த வலிமையை சோதிக்கிறது, நாடி ஆரோக்கியம் மற்றும் சந்ததியினருக்கான மரபணு பொருத்தத்தை உறுதி செய்கிறது.</p>
            </div>
            <div className="info-item">
              <h4>துல்லியமான முடிவுகள்</h4>
              <p>துல்லியமான முடிவுகளுக்கு, சரியான பிறந்த நேரம் மற்றும் இடத்தை வழங்கவும். சந்திரனின் நக்ஷத்திரம் மற்றும் ராசி உங்கள் ஜாதகத்திலிருந்து கணக்கிடப்படுகின்றன.</p>
            </div>
          </div>
        </motion.div>

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>இலவச திருமணப் பொருத்தம் - தமிழ் 12 பொருத்த சோதனை</h2>
          <p>ஆஸ்ட்ரோ ஈ-பஞ்சாங்கம் பாரம்பரிய தமிழ் 12 பொருத்த முறையைப் பயன்படுத்தி இலவச ஜாதக பொருத்தத்தை வழங்குகிறது. இது தென்னிந்திய வேத ஜோதிடத்தில் மிகவும் பரவலாகப் பின்பற்றப்படும் திருமணப் பொருத்த முறையாகும். எங்கள் கருவி மணப்பெண் மற்றும் மணமகனின் பிறந்த நட்சத்திரங்கள் (நக்ஷத்திரங்கள்), சந்திர ராசிகள் (ராசி) மற்றும் கிரக நிலைகளை பகுப்பாய்வு செய்து 12 முக்கிய பொருத்தக் காரணிகளை மதிப்பீடு செய்கிறது.</p>
          <p>12 பொருத்தங்களில் தினம், கணம், மகேந்திரம், ஸ்திரீ தீர்க்கம், யோனி, ராசி, ராசியாதிபதி, வச்யம், ராஜ்ஜு, வேதை, நாடி மற்றும் ஆயுள் ஆகியவை அடங்கும். இவற்றில், ராஜ்ஜு பொருத்தம் (திருமண பந்த வலிமை) மற்றும் நாடி பொருத்தம் (ஆரோக்கியம் மற்றும் சந்ததி பொருத்தம்) மிகவும் இன்றியமையாதவையாகக் கருதப்படுகின்றன. 12-ல் 8 அல்லது அதற்கு மேற்பட்ட மதிப்பெண் பொதுவாக சாதகமான பொருத்தமாகக் கருதப்படுகிறது.</p>
          <p>உடனடி பொருத்த பகுப்பாய்வு, விரிவான பொருத்த விவரம், மாங்கல்ய தோஷ சோதனை மற்றும் நிபுணர் பரிந்துரைகளைப் பெற மணப்பெண் மற்றும் மணமகன் இருவரின் பிறப்பு விவரங்களை உள்ளிடவும்.</p>

          <h3>12 பொருத்தங்களின் விரிவான விளக்கம்</h3>
          <p><strong>ராஜ்ஜு பொருத்தம் (மிக முக்கியம்):</strong> ராஜ்ஜு கணவனின் நீண்ட ஆயுளையும் பாதுகாப்பையும் ஆராய்கிறது. 27 நட்சத்திரங்கள் ஐந்து ராஜ்ஜு வகைகளாகப் பிரிக்கப்படுகின்றன (சிரோ, கண்ட, உதர, கடி, பாத). சாதகமான பொருத்தத்திற்கு, மணப்பெண் மற்றும் மணமகன் ஒரே ராஜ்ஜு வகையைச் சேர்ந்தவர்களாக இருக்கக்கூடாது. அவ்வாறு இருந்தால், கணவனின் ஆரோக்கியத்தையும் ஆயுளையும் பாதிக்கும் பெரிய ராஜ்ஜு தோஷமாகக் கருதப்படுகிறது.</p>
          <p><strong>நாடி பொருத்தம் (மிக முக்கியம்):</strong> நாடி உடல் பொருத்தத்தையும் ஆரோக்கியமான சந்ததியின் சாத்தியக்கூறையும் தீர்மானிக்கிறது. 27 நட்சத்திரங்கள் மூன்று நாடிகளாகப் பிரிக்கப்படுகின்றன — ஆதி (வாதம்), மத்ய (பித்தம்) மற்றும் அந்த்ய (கபம்). இரு பங்காளர்களும் வெவ்வேறு நாடி வகைகளைக் கொண்டிருக்க வேண்டும். நாடி தோஷம் (ஒரே நாடி) குழந்தைகளின் ஆரோக்கியப் பிரச்சினைகளையும் திருமண மோதல்களையும் ஏற்படுத்தலாம். இது அனைத்து தோஷங்களிலும் மிகவும் தீவிரமானதாகக் கருதப்படுகிறது.</p>
          <p><strong>கண பொருத்தம்:</strong> கணம் நட்சத்திரங்களை மூன்று குழுக்களாக வகைப்படுத்துகிறது — தேவ (தெய்வீகம்), மனுஷ்ய (மனிதம்) மற்றும் ராக்ஷச (அசுரம்) — குணாதிசயத்தின் அடிப்படையில். தேவ-தேவ மற்றும் மனுஷ்ய-மனுஷ்ய சிறந்த இணைப்புகள். தேவ-மனுஷ்ய ஏற்றுக்கொள்ளத்தக்கது. தேவ-ராக்ஷச இணைப்புகள் சிக்கலானவை. இந்தப் பொருத்தம் மன மற்றும் குணாதிசய பொருத்தத்தை நிர்வகிக்கிறது.</p>
          <p><strong>தின பொருத்தம்:</strong> மணப்பெண்ணின் பிறந்த நட்சத்திரத்திலிருந்து மணமகனின் நட்சத்திரம் வரை எண்ணி 9-ஆல் வகுத்து கணக்கிடப்படுகிறது. மீதி (1,3,5,7) சுபமாகக் குறிக்கிறது. தின பொருத்தம் ஆரோக்கியம், அதிர்ஷ்டம் மற்றும் அன்றாட வாழ்க்கையில் ஒட்டுமொத்த பொருத்தத்தை குறிக்கிறது.</p>
          <p><strong>மகேந்திர பொருத்தம்:</strong> மணப்பெண்ணின் நட்சத்திரத்திலிருந்து மணமகனின் நட்சத்திரம் வரை எண்ணப்படுகிறது. எண்ணிக்கை 4, 7, 10, 13, 16, 19, 22 அல்லது 25 ஆக இருந்தால் சாதகமானது. மகேந்திர பொருத்தம் திருமணத்தில் நீண்ட ஆயுள், செல்வம் மற்றும் சந்ததியை உறுதி செய்கிறது.</p>
          <p><strong>யோனி பொருத்தம்:</strong> 27 நட்சத்திரங்கள் 14 விலங்கு குறியீடுகளாக (யோனிகள்) வகைப்படுத்தப்படுகின்றன. குதிரை-குதிரை, யானை-யானை போன்ற இணக்கமான யோனி ஜோடிகள் சிறந்தவை. பூனை-எலி, நாய்-மான் போன்ற விரோத யோனி இணைப்புகள் உடல் பொருத்தத்தில் உரசலைக் குறிக்கின்றன.</p>

          <h3>வட இந்திய அஷ்டகூட முறை vs தென்னிந்திய 12 பொருத்தம்</h3>
          <p>வட இந்திய அஷ்டகூட (8 கூட) முறை 36 புள்ளிகளின் மொத்த மதிப்பெண்ணுடன் 8 பொருத்தக் காரணிகளை மதிப்பீடு செய்கிறது. 18-க்கு மேல் ஏற்றுக்கொள்ளத்தக்கது, 24-க்கு மேல் சிறப்பு. 8 கூடங்கள்: வர்ணம் (1 புள்ளி), வச்யம் (2), தாரா (3), யோனி (4), கிரக மைத்ரி (5), கணம் (6), ராசி (7) மற்றும் நாடி (8). தென்னிந்திய 12 பொருத்த முறை ராஜ்ஜு, மகேந்திரம் மற்றும் ஸ்திரீ தீர்க்கம் போன்ற கூடுதல் காரணிகளுடன் விரிவான பகுப்பாய்வை வழங்குகிறது. இரு முறைகளும் ராஜ்ஜு மற்றும் நாடியை மிக முக்கியமான காரணிகளாக அடையாளம் காணுகின்றன.</p>

          <h3>திருமணப் பொருத்தம் பற்றி அடிக்கடி கேட்கப்படும் கேள்விகள்</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>திருமணத்திற்கு நல்ல பொருத்த மதிப்பெண் என்ன?</h4>
              <p>12-ல் 10 அல்லது அதற்கு மேற்பட்ட மதிப்பெண் சிறப்பானதாகக் கருதப்படுகிறது. 8-9 நல்லது மற்றும் பொதுவாக திருமணத்திற்கு ஏற்றுக்கொள்ளத்தக்கது. 6-7 சாதாரணமானது மற்றும் தனிப்பட்ட காரணிகளை மதிப்பாய்வு செய்ய ஜோதிடரை அணுக வேண்டும். 6-க்கு கீழ் பொதுவாக பரிந்துரைக்கப்படுவதில்லை. இருப்பினும், ராஜ்ஜு தோஷம் அல்லது நாடி தோஷம் (மதிப்பெண் எதுவாக இருந்தாலும்) தீவிரமானதாகக் கருதப்படுகிறது மற்றும் நிபுணர் ஆலோசனை தேவை.</p>
            </div>
            <div className="faq-seo-item">
              <h4>ராஜ்ஜு தோஷத்துடன் திருமணம் நடக்குமா?</h4>
              <p>ராஜ்ஜு தோஷம் பாரம்பரியமாக மிகவும் தீவிரமானதாகக் கருதப்படுகிறது. இருப்பினும், தனிப்பட்ட ஜாதகங்களில் சில கிரக நிலைகள் ராஜ்ஜு தோஷத்தை நடுநிலையாக்கலாம் — உதாரணமாக, குரு 7-ம் வீட்டைப் பார்த்தால் அல்லது சுக்கிரன் மிகவும் வலுவாக இருந்தால். சில மரபுகளில், குறிப்பிட்ட சடங்குகள் மற்றும் பரிகாரங்கள் பரிந்துரைக்கப்படுகின்றன. இறுதி முடிவுக்கு முன் அனுபவமுள்ள ஜோதிடர் முழு ஜாதகங்களை மதிப்பீடு செய்ய வேண்டும்.</p>
            </div>
            <div className="faq-seo-item">
              <h4>மாங்கல்ய தோஷம் என்றால் என்ன?</h4>
              <p>மாங்கல்ய தோஷம் மற்றும் குஜ தோஷம் இரண்டும் ஒரே நிலையைக் குறிக்கின்றன — செவ்வாய் (மங்கள்/குஜ) ஜாதகத்தின் 1, 2, 4, 7, 8 அல்லது 12-வது வீட்டில் அமர்ந்திருப்பது. இது திருமணத்தில் தடைகளை உருவாக்கும் என்று நம்பப்படுகிறது. பாரம்பரிய பரிகாரம் என்னவென்றால், ஒரு மாங்கல்ய நபர் மற்றொரு மாங்கல்ய நபரை மணந்தால் தோஷம் நீங்கும். பல நவீன ஜோதிடர்கள் முழு மாங்கல்ய தோஷத்தை அறிவிக்கும் முன் செவ்வாயின் வலிமையையும் சுபப் பார்வைகளையும் கருத்தில் கொள்கின்றனர்.</p>
            </div>
            <div className="faq-seo-item">
              <h4>ஜாதக பொருத்தத்திற்கு என்ன தகவல் தேவை?</h4>
              <p>மணப்பெண் மற்றும் மணமகன் இருவருக்கும் சரியான பிறந்த தேதி, பிறந்த நேரம் மற்றும் பிறந்த இடம் தேவை. பிறந்த நட்சத்திரம் (நக்ஷத்திரம்) மற்றும் சந்திர ராசி (ராசி) இந்த தரவிலிருந்து தானாகவே கணக்கிடப்படும். அதிகபட்ச துல்லியத்திற்கு, பிறந்த நேரம் சரியாக இருப்பதை உறுதிப்படுத்தவும் — 30 நிமிட வேறுபாடு கூட சில நேரங்களில் நட்சத்திர பாதத்தை மாற்றி, சில பொருத்தக் கணக்கீடுகளை பாதிக்கலாம்.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JathagamPoruthamTamil;
